// Shared geometry/formatting for the hand-built SVG charts on Tren
// Pendapatan (RevenueCompositionChart, SessionsCompletedChart). Plain SVG, no
// charting library — this app's Analytics reports are table/card-first, so
// a chart is only justified where the *shape* of a trend is the point, and
// pulling in a dependency for two charts isn't.

// Internal coordinate space, scaled UNIFORMLY (default preserveAspectRatio
// "xMidYMid meet" — deliberately not "none") to fit each chart's box. "none"
// independently stretches x and y to fill any container, which distorts
// circle markers into ellipses and skews stroke width on diagonal segments
// the moment the container's aspect ratio doesn't match the viewBox — this
// happened at full report-page width. Picked to roughly match a half-width
// column (see SalesTrendPage's side-by-side layout) so the letterboxing
// "meet" can introduce stays minimal.
export const VBOX_W = 520;
export const VBOX_H = 200;
export const PAD_LEFT = 56;
export const PAD_RIGHT = 12;
export const PAD_TOP = 16;
export const PAD_BOTTOM = 28;
export const PLOT_W = VBOX_W - PAD_LEFT - PAD_RIGHT;
export const PLOT_H = VBOX_H - PAD_TOP - PAD_BOTTOM;

// Compact Rupiah for axis labels/tooltips — "1.2 jt" reads faster on a
// chart than the full "Rp 1.200.000" used in the app's tables.
export const formatShort = (n: number) => {
  if (n >= 1_000_000_000) return `Rp ${(n / 1_000_000_000).toFixed(1).replace(/\.0$/, "")} M`;
  if (n >= 1_000_000) return `Rp ${(n / 1_000_000).toFixed(1).replace(/\.0$/, "")} jt`;
  if (n >= 1_000) return `Rp ${Math.round(n / 1_000)} rb`;
  return `Rp ${Math.round(n)}`;
};

// Rounds a max value up to the nearest "nice" number, using a finer ladder
// than the usual 1/2/5/10 so the headroom above the tallest point never
// balloons past ~25% (a coarse ladder can round e.g. 20.2 up to 50, wasting
// more than half the chart on empty space).
const NICE_STEPS = [1, 1.2, 1.5, 2, 2.5, 3, 4, 5, 6, 8, 10];
export const niceMax = (maxVal: number) => {
  if (maxVal <= 0) return 1;
  const pow = Math.pow(10, Math.floor(Math.log10(maxVal)));
  const n = maxVal / pow;
  const nice = NICE_STEPS.find((step) => step >= n) ?? 10;
  return nice * pow;
};

// Axis ticks for a chart whose y values are COUNTS (people, sessions) rather
// than money. niceMax is wrong for these twice over: its ladder includes
// fractional steps (1.2, 1.5, 2.5), and the usual [0, 0.5, 1] gridline trio
// then rounds the midpoint for display — so a max of 1 renders as "1, 1, 0",
// two of which are the same number at different heights.
//
// This returns integer ticks with an integer step, so every gridline label is
// distinct and lands exactly where it says. `top` is the value to scale
// against (>= the data max, never 0).
const COUNT_STEPS = [1, 2, 5, 10, 20, 25, 50, 100, 200, 250, 500, 1000];
export const countTicks = (dataMax: number) => {
  const m = Math.max(1, Math.ceil(dataMax));
  // At most 4 intervals, so a small count keeps a readable ladder (a max of
  // 3 gets 0/1/2/3, not 0/1.5/3).
  const step = COUNT_STEPS.find((s) => m / s <= 4) ?? Math.ceil(m / 4);
  const top = Math.ceil(m / step) * step;
  const ticks: number[] = [];
  for (let v = 0; v <= top; v += step) ticks.push(v);
  return { top, ticks };
};

// x-position for the i-th of n evenly spaced POINTS across the plot area —
// a point scale (first/last point sit exactly on the plot edges), the right
// mapping for line/area charts (buildLinePath/buildAreaPath). Not for bars:
// a bar centered on an edge point sticks out half a bar past the plot on
// both ends, painting the first bar over the y-axis labels.
export const xFor = (i: number, n: number) =>
  n <= 1 ? PAD_LEFT + PLOT_W / 2 : PAD_LEFT + (i / (n - 1)) * PLOT_W;

// Band scale for bar charts: each of n categories owns an equal-width band
// of the plot, with its bar centered inside — so any bar no wider than its
// band stays fully inside the plot area by construction.
//
// plotW defaults to the shared PLOT_W. Pass it explicitly from a chart that
// sizes its own viewBox to the container (StampDistributionChart), so the
// bands stretch instead of the whole figure being scaled up.
export const bandWidth = (n: number, plotW: number = PLOT_W) =>
  plotW / Math.max(n, 1);
export const bandXFor = (i: number, n: number, plotW: number = PLOT_W) =>
  PAD_LEFT + (i + 0.5) * bandWidth(n, plotW);

// y-position for a value against a 0..max scale.
export const yFor = (value: number, max: number) =>
  PAD_TOP + (1 - value / max) * PLOT_H;

// Closed polygon path for the area between two boundary lines (upper trace
// left-to-right, lower trace back right-to-left) — the standard technique
// for a filled area/stacked-area chart in hand-built SVG.
export const buildAreaPath = (
  points: { x: number; lowerY: number; upperY: number }[],
) => {
  const upper = points.map((p) => `${p.x},${p.upperY}`).join(" L ");
  const lower = [...points].reverse().map((p) => `${p.x},${p.lowerY}`).join(" L ");
  return `M ${upper} L ${lower} Z`;
};

// Open polyline path for a boundary/stroke line (no fill).
export const buildLinePath = (points: { x: number; y: number }[]) =>
  `M ${points.map((p) => `${p.x},${p.y}`).join(" L ")}`;
